import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";

// Each email is a list of segments so specific phrases can be underlined.
// { t: text, u: true } renders underlined; plain { t } renders normally.
const EMAILS = [
  {
    id: "david-h",
    signature: ["DAVID H."],
    lines: [
      [{ t: "Paul," }],
      [
        { t: "Thanks for your work on the " },
        {
          t: "JobInfoNetwork… Through this point of contact, I have gotten several leads",
          u: true,
        },
        {
          t: " on specific jobs, pointers on other places to look based on my experience, suggestions for my resume, ",
        },
        { t: "and as of this week, A JOB.", u: true },
      ],
    ],
  },
  {
    id: "jim-h",
    signature: ["Jim H.", "Bothell 2nd Ward", "Bothell Washington Stake"],
    lines: [
      [{ t: "Dear Paul," }],
      [
        { t: "…First the facts.   I was able to find employment", u: true },
        {
          t: " through the referrals provided by one group of ten resume submissions and had two more strong expressions of interest that simply did not mature as fast as the offer I eventually accepted…",
        },
      ],
    ],
  },
  {
    id: "curt-j",
    signature: ["Curt J."],
    lines: [
      [{ t: "Hi," }],
      [
        {
          t: "Thanks to the Job Info Network, I'm starting a new permanent position tomorrow",
          u: true,
        },
        { t: "..." },
      ],
      [{ t: "My thanks to Paul and everyone else who is helping with this!" }],
    ],
  },
  {
    id: "gary-t",
    signature: ["Gary T.", "Bothell Stake"],
    lines: [
      [{ t: "Brother Jarvis." }],
      [
        { t: "…" },
        {
          t: "I got something like 7 phone and live interviews from JobInfoNetwork.",
          u: true,
        },
        {
          t: "  I learned a lot from the people I spoke with about resumes, cover letters, interviews and networking.  All of this cost me nothing.",
        },
      ],
      [{ t: "… I got an email (job posting) prompt from JIN..." }],
      [
        { t: "…Afterward, " },
        { t: "the CEO took me to lunch and offered me the job", u: true },
        {
          t: "… I started the next day with a salary and title commensurate with my experience and abilities. ..",
        },
      ],
      [{ t: "Again, I can't thank you enough." }],
    ],
  },
];

const FADE_MS = 900;
const HOLD_MS = 7000;
const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

// Each email rises into place, holds, then lifts away — blur and scale on the
// way in and out make the swap read as a deliberate transition, not a blink.
const PHASE_STYLES = {
  in: {
    opacity: 0,
    transform: "translateY(40px) scale(0.94)",
    filter: "blur(10px)",
  },
  reveal: { opacity: 1, transform: "translateY(0) scale(1)", filter: "blur(0px)" },
  hold: { opacity: 1, transform: "translateY(0) scale(1)", filter: "blur(0px)" },
  out: {
    opacity: 0,
    transform: "translateY(-40px) scale(0.94)",
    filter: "blur(10px)",
  },
};

const EmailTestimonials = ({ onComplete }) => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  // in -> reveal -> hold -> out -> (advance). Splitting the cycle into phases
  // keeps each timer owned by exactly one phase, so pausing during `hold`
  // can't cancel a timer belonging to another stage.
  const [phase, setPhase] = useState("in");

  // Restart the cycle whenever we move to a new email
  useEffect(() => {
    setPhase("in");
  }, [index]);

  useEffect(() => {
    if (phase === "in") {
      const t = setTimeout(() => setPhase("reveal"), 50);
      return () => clearTimeout(t);
    }
    if (phase === "reveal") {
      // Wait out the fade-in so the hold below is full-opacity reading time
      const t = setTimeout(() => setPhase("hold"), FADE_MS);
      return () => clearTimeout(t);
    }
    if (phase === "hold") {
      if (paused) return undefined;
      const t = setTimeout(() => setPhase("out"), HOLD_MS);
      return () => clearTimeout(t);
    }
    // phase === "out"
    const t = setTimeout(() => {
      if (index < EMAILS.length - 1) {
        setIndex((prev) => prev + 1);
      } else if (onComplete) {
        onComplete();
      }
    }, FADE_MS);
    return () => clearTimeout(t);
    // `onComplete` is intentionally excluded — the parent passes an inline
    // arrow, so including it would reset the timers on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, paused, index]);

  const email = EMAILS[index];

  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          ...PHASE_STYLES[phase],
          transition: `opacity ${FADE_MS}ms ${EASE}, transform ${FADE_MS}ms ${EASE}, filter ${FADE_MS}ms ${EASE}`,
          willChange: "opacity, transform, filter",
          maxWidth: "900px",
          margin: "0 auto",
          padding: "0 20px",
          fontSize: "20px",
          lineHeight: 1.6,
        }}
      >
        {email.lines.map((line, lineIndex) => (
          <p key={lineIndex} className="mb-3">
            {line.map((seg, segIndex) => (
              <span
                key={segIndex}
                style={
                  seg.u
                    ? { textDecoration: "underline", fontWeight: 600 }
                    : undefined
                }
              >
                {seg.t}
              </span>
            ))}
          </p>
        ))}
        <div className="mt-4">
          {email.signature.map((line, sigIndex) => (
            <div key={sigIndex}>{line}</div>
          ))}
        </div>
      </div>

      {/* Progress dots */}
      <div className="text-center mt-4">
        {EMAILS.map((e, dotIndex) => (
          <span
            key={e.id}
            style={{
              display: "inline-block",
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              margin: "0 5px",
              backgroundColor:
                dotIndex === index ? "var(--blue, #1e5799)" : "#ccc",
              transition: "background-color 300ms ease",
            }}
          />
        ))}
      </div>

      <div className="text-center mt-3">
        <Button
          variant={paused ? "success" : "outline-secondary"}
          onClick={() => setPaused((prev) => !prev)}
        >
          {paused ? "Resume" : "Pause"}
        </Button>
      </div>
    </div>
  );
};

export default EmailTestimonials;
